import { useState, useEffect } from 'react';
import Joi from 'joi';
import { toast } from 'react-toastify';
import { Link, Redirect } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';

import { getTeam, createTeam } from '../../../services/teamService';
import userService from '../../../services/userService';
import auth from '../../../services/authService';
import useForm from '../../../hooks/useForm';
import { renderInput, renderButton } from '../../common/renderUtils';
import FormHeader from '../../common/FormHeader';
import PasswordRequirements from '../../common/PasswordRequirements';
import SignUpClosedNotice from '../../common/SignUpClosedNotice';
import TeamOptions from './components/TeamOptions';
import JoinTeam from './components/JoinTeam';
import CreateTeam from './components/CreateTeam';
import { setDocTitle } from '../../../utils/utilities';
import { useGlobalContext, signUpEnabled } from '../../../context/GlobalContext';
import { saveWelcomeModalData } from '../../../helpers/welcomeModal';
import { newPasswordSchema } from '../../../helpers/passwordSchema';

import './SignUp.scss';

const SignUp = (props) => {
  const { t } = useTranslation();
  const existingUser = auth.getCurrentUser();
  const isJoiningTeam = props.location.pathname === '/join-team';
  const { setShowRemovedFromTeamModal } = useGlobalContext();

  const [team, setTeam] = useState({ _id: '', name: '' });
  // If user is joining a team (already authenticated), start at step 2
  const [step, setStep] = useState(isJoiningTeam ? 2 : 1);
  const [callingApi, setCallingApi] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  const schema = {
    firstName: Joi.string().min(2).max(50).required().label('First Name'),
    lastName: Joi.string().min(2).max(50).required().label('Last Name'),
    email: Joi.string()
      .min(5)
      .max(255)
      .required()
      .email({ tlds: { allow: false } })
      .label('Email Address'),
    ...newPasswordSchema(),
  };

  const initialData = {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  };

  const { data, errors, setErrors, handleChange, handleSubmit, isFormValid } = useForm(initialData, schema);

  // Hide the password validation error while the field is empty (requirements are shown instead)
  const passwordErrors = { ...errors, password: data.password ? '' : errors.password };
  const passwordFocusHandlers = {
    onFocus: () => setPasswordFocused(true),
    onBlur: () => setPasswordFocused(false),
  };

  useEffect(() => {
    const populateTeam = async () => {
      const teamId = props.match.params.teamId;

      if (teamId) {
        try {
          setCallingApi(true);

          const { data: validTeam } = await getTeam(teamId);

          setTeam({
            _id: validTeam._id,
            name: validTeam.name,
          });
          setCallingApi(false);
        } catch (ex) {
          toast.error(t('auth.invalidTeamId'));
          setCallingApi(false);
        }
      }
    };

    populateTeam();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetching on a language change would toast again
  }, [props.match.params.teamId]);

  useEffect(() => {
    setDocTitle(t('auth.signUp'));
  }, [t]);

  const populateTeam = async (id) => {
    const teamId = props.match.params.teamId || id;

    if (teamId) {
      try {
        setCallingApi(true);

        const { data: validTeam } = await getTeam(teamId);

        setTeam({
          _id: validTeam._id,
          name: validTeam.name,
        });
        setCallingApi(false);
      } catch (ex) {
        toast.error(t('auth.invalidTeamId'));
        setCallingApi(false);
      }
    }
  };

  const handleShowJoinTeam = () => {
    setStep(3);
  };

  const handleShowCreateTeam = () => {
    setStep(4);
  };

  const handleShowTeamOptions = () => {
    setStep(2);
  };

  const handleShowSignUpForm = () => {
    setStep(1);
  };

  const handleJoinTeam = async (teamId) => {
    // If user is already authenticated (joining new team), update their team
    if (isJoiningTeam && existingUser) {
      try {
        setCallingApi(true);

        const { data: validTeam } = await getTeam(teamId);
        const { headers } = await userService.updateUserTeam(existingUser._id, validTeam._id, false);

        auth.signinWithJwt(headers['x-auth-token']);
        setShowRemovedFromTeamModal(false);
        toast.success(t('auth.joinedTeam', { team: validTeam.name }));
        props.history.push('/songs');
      } catch (ex) {
        if (ex.response && (ex.response.status === 404 || ex.response.status === 400)) {
          toast.error(t('auth.invalidTeamId'));
        } else if (ex.response && ex.response.status === 403) {
          toast.error(t('auth.teamClosed'));
        }
        setCallingApi(false);
      }
    } else {
      // Original behavior for new users
      setStep(1);
      populateTeam(teamId);
    }
  };

  const handleCreateTeam = async (teamName) => {
    if (!signUpEnabled) {
      alert(t('auth.signUpClosed'));
      return;
    }

    try {
      setCallingApi(true);
      const { data: newTeam } = await createTeam(teamName);

      const createdTeam = {
        _id: newTeam._id,
        name: newTeam.name,
        isAdmin: true,
      };

      setTeam(createdTeam);

      // If user is already authenticated, update their team
      if (isJoiningTeam && existingUser) {
        const { headers } = await userService.updateUserTeam(existingUser._id, newTeam._id, true);

        auth.signinWithJwt(headers['x-auth-token']);
        setShowRemovedFromTeamModal(false);
        toast.success(t('auth.createdTeam', { team: newTeam.name }));
        props.history.push('/songs');
      } else {
        doSubmit(createdTeam);
      }
    } catch (ex) {
      if (ex.response && ex.response.status === 400) {
        toast.error(t('auth.createTeamFailed'));
      }
      setCallingApi(false);
    }
  };

  const doSubmit = async (targetTeam = team) => {
    // Check email availability
    try {
      setCallingApi(true);

      const { data: availableEmail } = await userService.available(data.email);

      if (!availableEmail) {
        const newErrors = { ...errors };
        newErrors.email = t('auth.emailTaken');

        setErrors(newErrors);
        setCallingApi(false);

        return;
      }
      setCallingApi(false);
    } catch (ex) {
      setCallingApi(false);

      return;
    }

    if (targetTeam._id) {
      const user = { ...data };
      user.teamId = targetTeam._id;

      if (targetTeam.isAdmin) user.isAdmin = targetTeam.isAdmin;

      try {
        setCallingApi(true);

        const { headers } = await userService.register(user);

        auth.signinWithJwt(headers['x-auth-token']);

        // Save welcome modal data to localStorage for display after page reload
        try {
          const { data: fullTeamData } = await getTeam(targetTeam._id);

          saveWelcomeModalData({
            firstName: data.firstName,
            teamName: fullTeamData.name,
            teamMembers: fullTeamData.users || [],
            isAdmin: targetTeam.isAdmin || false,
          });
        } catch (ex) {
          // If fetching team data fails, still redirect without welcome modal
        }

        window.location = '/songs';
      } catch (ex) {
        if (ex.response && ex.response.status === 403) {
          toast.error(t('auth.namedTeamClosed', { team: targetTeam.name }), {
            autoClose: 10000,
          });
        }

        setCallingApi(false);
      }
    } else {
      // If there isn't a team id take the user to join or create a team
      handleShowTeamOptions();
    }
  };

  // If there is an existing user and they're NOT joining a team, redirect to home
  if (auth.getCurrentUser() && !isJoiningTeam) return <Redirect to="/" />;

  switch (step) {
    case 1:
      return (
        <div className="auth-page SignUp">
          <form onSubmit={(e) => handleSubmit(e, doSubmit)} className="form-signup">
            <FormHeader title={t('auth.createAccount')} />
            {team._id ? (
              <div className="joining-team">
                <span className="text">
                  <span className="step">{t('auth.lastStep')}</span>
                  <span className="name">
                    <Trans i18nKey="auth.joining" values={{ team: team.name }} components={{ strong: <strong /> }} />
                  </span>
                </span>
                <button type="button" className="change" onClick={handleShowJoinTeam}>
                  {t('auth.change')}
                </button>
              </div>
            ) : (
              <SignUpClosedNotice />
            )}
            <div className="mg-form-row">
              <div className="mg-col-6">
                {renderInput('firstName', t('auth.firstName'), 'text', data, errors, handleChange)}
              </div>
              <div className="mg-col-6">
                {renderInput('lastName', t('auth.lastName'), 'text', data, errors, handleChange)}
              </div>
            </div>
            {renderInput('email', t('auth.emailAddress'), 'email', data, errors, handleChange)}
            {renderInput(
              'password',
              t('auth.password'),
              'password',
              data,
              passwordErrors,
              handleChange,
              passwordFocusHandlers,
            )}
            <PasswordRequirements password={data.password} show={passwordFocused || !!data.password} />
            {renderInput('confirmPassword', t('auth.confirmPassword'), 'password', data, errors, handleChange)}
            {renderButton(
              team._id ? t('auth.joinTeamName', { team: team.name }) : t('auth.continue'),
              isFormValid(),
              callingApi,
              team._id && 'join-btn',
            )}

            <div className="mg-text-center mg-mt-2">
              <Trans i18nKey="auth.haveAccount" components={{ go: <Link to="/signin" /> }} />
            </div>
          </form>
        </div>
      );
    case 2:
      const firstName = isJoiningTeam && existingUser ? existingUser.firstName : data.firstName;
      const onGoBack = isJoiningTeam ? () => props.history.push('/songs') : handleShowSignUpForm;

      return (
        <div className="auth-page">
          <TeamOptions
            firstName={firstName}
            onShowJoinTeam={handleShowJoinTeam}
            onShowCreateTeam={handleShowCreateTeam}
            onGoBack={onGoBack}
          />
        </div>
      );
    case 3:
      return (
        <div className="auth-page">
          <JoinTeam onJoinTeam={handleJoinTeam} onGoBack={handleShowTeamOptions} />
        </div>
      );
    case 4:
      return (
        <div className="auth-page">
          <CreateTeam onCreateTeam={handleCreateTeam} onGoBack={handleShowTeamOptions} callingApi={callingApi} />
        </div>
      );

    default:
      return null;
  }
};

export default SignUp;
